import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type {
  Plan,
  PlanCloneInput,
  PlanCloneRequestBody,
  PlanCreateInput,
  PlanDeletePayload,
  PlanListFilters,
  PlanPatch,
  PlanWrite,
  UUID,
} from './types';

interface PlanListResponse {
  items: Plan[];
  total: number;
}

export function usePlaeneList(filters: PlanListFilters) {
  return useQuery({
    queryKey: queryKeys.plaene.list(filters),
    queryFn: async (): Promise<Plan[]> => {
      const params: Record<string, unknown> = {};
      if (filters.einrichtung_id) params.einrichtung_id = filters.einrichtung_id;
      if (filters.foerderjahr) params.foerderjahr = filters.foerderjahr;
      const res = await apiClient.get<PlanListResponse>('/plan', { params });
      return res.data.items ?? [];
    },
    staleTime: 30_000,
  });
}

export function usePlan(id: UUID | null) {
  return useQuery({
    queryKey: id ? queryKeys.plaene.detail(id) : ['plaene', 'detail', 'none'],
    queryFn: async (): Promise<Plan> => {
      if (!id) throw new Error('id required');
      const res = await apiClient.get<Plan>(`/plan/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PlanCreateInput | PlanWrite): Promise<Plan> => {
      // Backend accepts only einrichtung_id, foerderjahr, name. Other wizard
      // fields (template_options, source) are kept client-side.
      const body: PlanWrite = {
        einrichtung_id: input.einrichtung_id,
        foerderjahr: input.foerderjahr,
        name: input.name,
      };
      const res = await apiClient.post<Plan>('/plan', body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.all });
    },
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: PlanPatch }): Promise<Plan> => {
      const res = await apiClient.patch<Plan>(`/plan/${vars.id}`, vars.patch);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.all });
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.detail(vars.id) });
      // mpz_anteil fließt in die Personalbedarf-Rechnung ein.
      void qc.invalidateQueries({
        queryKey: queryKeys.belegung.personalbedarfAlle(vars.id),
      });
      // schwerpunkt_kinder_anzahl steht ebenfalls am Plan und bestimmt die
      // Schwerpunktkita-Pauschale. Ohne diese Zeile blieb der Betrag im
      // Reiter "Einnahmen Land" auf dem alten Stand, bis die Seite neu
      // geladen wurde — der Prozentsatz daneben stammt aus dem
      // Eingabefeld und sah deshalb schon richtig aus.
      void qc.invalidateQueries({
        queryKey: queryKeys.einnahmen.landAufschluesselung(vars.id),
      });
    },
  });
}

export function useClonePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PlanCloneInput | (PlanCloneRequestBody & { source_plan_id: UUID })): Promise<Plan> => {
      // Map legacy and new shapes onto the backend POST /plan/{id}/clone body.
      const sourceId =
        'source_plan_id' in input
          ? input.source_plan_id
          : (input as PlanCloneInput).source_plan_id;
      const body: PlanCloneRequestBody = {
        neuer_name:
          'neuer_name' in input
            ? input.neuer_name
            : (input as PlanCloneInput).new_name,
        neues_foerderjahr:
          'neues_foerderjahr' in input
            ? input.neues_foerderjahr
            : (input as PlanCloneInput).target_foerderjahr,
      };
      const res = await apiClient.post<Plan>(`/plan/${sourceId}/clone`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.all });
    },
  });
}

export function useFinalizePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<Plan> => {
      const res = await apiClient.post<Plan>(`/plan/${id}/finalize`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.all });
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.detail(id) });
    },
  });
}

export function useRecalcPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<unknown> => {
      const res = await apiClient.post<unknown>(`/plan/${id}/recalc`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(id) });
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID } & PlanDeletePayload): Promise<void> => {
      await apiClient.delete(`/plan/${vars.id}`, {
        data: { reason: vars.reason, confirm_name: vars.confirm_name },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.plaene.all });
    },
  });
}
